import { VStack } from '~/components/stack';
import {
  LegalParagraph,
  LegalSection,
  LegalSubsection,
  ListItem,
  UnorderedList,
} from '~/routes/legal/list';

function LegalTermsOfServicePage() {
  return (
    <VStack className="gap-8">
      <VStack className="gap-4">
        <h1 className="text-3xl font-bold">Terms of use</h1>
        <span className="text-muted-foreground">
          Effective Date: 1st September 2024
        </span>
        <LegalParagraph>
          Welcome to MEDizzy Learning (“Platform”), a comprehensive and advanced
          learning resource designed to assist you in preparing for the Medical
          Licensing Examination Preparation operated by MEDizzy Ltd., 85
          Uxbridge Road, W5 5TH London, United Kingdom, Company number: 10143587
          (“Company”).
        </LegalParagraph>
        <LegalParagraph>
          By accessing or using our Platform, you signify your agreement to
          comply with and be legally bound by the terms and conditions set forth
          below (“Terms”). Please read these Terms thoroughly before using the
          Platform. If you do not agree to these Terms, do not use the Platform.
        </LegalParagraph>
      </VStack>
      <LegalSection title="1. Acceptance of Terms">
        <LegalParagraph>
          By accessing or using any part of the Platform, you agree to be bound
          by these Terms, the MEDizzy Learning Privacy Policy, and all other
          operating rules and policies that may be published from time to time
          on the Platform. Your use of the Platform is at your sole risk.
        </LegalParagraph>
      </LegalSection>
      <LegalSection title="2. Eligibility">
        <LegalParagraph>
          The Platform is intended for use by medical students, residents, and
          healthcare professionals. By accessing the Platform, you affirm that
          you are at least 18 years of age, are legally able to enter into a
          binding contract, and are using the Platform for educational and
          professional purposes.
        </LegalParagraph>
      </LegalSection>
      <LegalSection title="3. Account Registration and Security">
        <LegalParagraph>
          To access certain features of the Platform, you will need to register
          for an account. When creating your account, you agree to:
        </LegalParagraph>
        <UnorderedList>
          <ListItem>
            Provide truthful, accurate, current, and complete information.
          </ListItem>
          <ListItem>
            Maintain and promptly update your account information to ensure it
            remains accurate, current, and complete.
          </ListItem>
          <ListItem>
            Maintain the confidentiality of your login credentials.
          </ListItem>
          <ListItem>
            Immediately notify us if you suspect any unauthorized use of your
            account or any other breach of security.
          </ListItem>
        </UnorderedList>
        <LegalParagraph>
          You are solely responsible for all activities that occur under your
          account, and MEDi will not be liable for any loss or damage arising
          from any unauthorized use of your account.
        </LegalParagraph>
      </LegalSection>
      <LegalSection title="4. Use of the Platform">
        <LegalParagraph>
          You agree to use the Platform in compliance with all applicable laws,
          regulations, and these Terms. In addition, you agree not to:
        </LegalParagraph>
        <UnorderedList>
          <ListItem>
            Use the Platform in any manner that could interfere with, disrupt,
            negatively affect, or inhibit other users from fully enjoying the
            Platform.
          </ListItem>
          <ListItem>
            Use the Platform for any fraudulent or unlawful purpose.
          </ListItem>
          <ListItem>
            Attempt to gain unauthorized access to the Platform or related
            systems or networks.
          </ListItem>
          <ListItem>
            Upload or transmit any harmful code, including viruses, worms, or
            other malicious software.
          </ListItem>
          <ListItem>
            Use any automated system, including robots, spiders, or offline
            readers, to access the Platform for any purpose without our express
            written permission.
          </ListItem>
          <ListItem>
            Circumvent or attempt to circumvent any measures that we may use to
            prevent or restrict access to the Platform.
          </ListItem>
        </UnorderedList>
      </LegalSection>
      <LegalSection title="5. Intellectual Property Rights">
        <LegalParagraph>
          All content on the Platform, such as text, graphics, logos, images,
          videos, and software, is the property of Company or its licensors and
          is protected by intellectual property laws. You acknowledge and agree
          that:
        </LegalParagraph>
        <UnorderedList>
          <ListItem>
            You are granted a limited, non-exclusive, non-transferable, and
            revocable license to access and use the Platform for your personal,
            non-commercial educational purposes.
          </ListItem>
          <ListItem>
            You will not copy, modify, distribute, sell, lease, or make
            derivative works of any part of the Platform without our prior
            written consent.
          </ListItem>
          <ListItem>
            No ownership rights are transferred to you by accessing or using the
            Platform.
          </ListItem>
        </UnorderedList>
      </LegalSection>
      <LegalSection title="6. Subscription, Payment, and Refund Policy">
        <LegalSubsection title="6.1 Subscription Plans">
          <LegalParagraph>
            The Platform offers various subscription plans to provide access to
            premium features including, but not limited to:
          </LegalParagraph>
          <UnorderedList>
            <ListItem>Full access to question banks.</ListItem>
            <ListItem>Extended learning materials.</ListItem>
            <ListItem>Advanced analytics and progress tracking.</ListItem>
            <ListItem>Personalized study plans.</ListItem>
            <ListItem>Continuous support and updates.</ListItem>
          </UnorderedList>
          <LegalParagraph>
            Subscription plans may be available on a monthly, quarterly, or
            annual basis. Specific terms and features are outlined during the
            subscription process.
          </LegalParagraph>
        </LegalSubsection>
        <LegalSubsection title="6.2 Payment Information">
          <LegalParagraph>
            To purchase a subscription, you must provide valid and accurate
            payment information. By subscribing, you agree to:
          </LegalParagraph>
          <UnorderedList>
            <ListItem>
              Authorize the Company or its third-party payment processors to
              charge you for the subscription plan you selected.
            </ListItem>
            <ListItem>Keep your payment information up to date.</ListItem>
            <ListItem>
              Pay all fees and charges associated with your subscription,
              including any applicable taxes.
            </ListItem>
          </UnorderedList>
        </LegalSubsection>
        <LegalSubsection title="6.3 Automatic Renewal">
          <LegalParagraph>
            Unless otherwise specified, all subscriptions automatically renew at
            the end of the applicable subscription period. You will be charged
            the subscription fees in effect at the time of renewal. You may
            cancel the auto-renewal of your subscription at any time before the
            renewal date through your account settings or by contacting customer
            support.
          </LegalParagraph>
        </LegalSubsection>
        <LegalSubsection title="6.4 Refund Policy">
          <LegalParagraph>
            All payments are non-refundable unless required by law or as
            specified in specific terms and conditions of the service plan you
            purchase. In certain cases, we may, in our sole discretion, offer
            refunds or credits.
          </LegalParagraph>
        </LegalSubsection>
        <LegalSubsection title="6.5 Promotions and Discounts">
          <LegalParagraph>
            We may, at our discretion, offer promotions and discounts on
            subscriptions. These promotions are subject to specific terms and
            may be available for a limited time. Discount codes and promotions
            cannot be applied to past purchases or combined with other offers,
            unless stated otherwise.
          </LegalParagraph>
        </LegalSubsection>
      </LegalSection>
      <LegalSection title="7. Content Quality and Editorial Process">
        <LegalSubsection title="7.1 Professional Editorial Team">
          <LegalParagraph>
            The professional editorial team of the Platform consists of a large
            number of medical professionals with various levels of experience
            and from different medical specialties. This diversity ensures that
            the content is comprehensive, accurate, and relevant to a wide range
            of medical fields and examination needs.
          </LegalParagraph>
        </LegalSubsection>
        <LegalSubsection title="7.2 Content Addition and Updates">
          <LegalParagraph>
            With high frequency, new and/or updated content is added to the
            Platform database by this editorial team in a structured,
            collaborative process and thus made available to authorized users.
            This ensures that users have access to the latest information and
            resources for their exam preparation.
          </LegalParagraph>
        </LegalSubsection>
        <LegalSubsection title="7.3 Peer Review Process">
          <LegalParagraph>
            All content is subject to a multi-stage internal peer review process
            which checks all publications for scientific up-to-dateness,
            consistency, and comprehensibility. This rigorous review process is
            carried out with reasonable care to maintain the highest standards
            of quality.
          </LegalParagraph>
        </LegalSubsection>
        <LegalSubsection title="7.4 Limitations of Content">
          <LegalParagraph>
            Despite our best efforts, medical knowledge is constantly evolving,
            and the Platform database – simply because of the abundance of new
            medical information – cannot contain a comprehensive, up-to-date
            representation of all medical knowledge. Users are advised to use
            the Platform in conjunction with other reputable sources of medical
            information and to verify the data as needed.
          </LegalParagraph>
        </LegalSubsection>
        <LegalSubsection title="7.5 Diagnostic and Treatment Disclaimer">
          <LegalParagraph>
            The information provided on the Platform is not intended to serve as
            definitive diagnostic, treatment, or therapeutic recommendations.
            All medical decisions, including diagnoses and treatment plans, as
            well as other patient care choices made while using the Platform,
            are the sole responsibility of the user. Utilizing the Platform does
            not absolve users from their duty to make decisions independently,
            based on the latest scientific research and medical guidelines.
            Therefore, the Platform's information should be carefully evaluated
            in the context of each unique patient case, considering individual
            patient characteristics and circumstances.
          </LegalParagraph>
          <LegalParagraph>
            Furthermore, all information provided on the Platform refers to the
            medical standards in the United States. This means that the
            standards stated, such as those on diagnostic and/or therapeutic
            procedures, may differ in other countries. Similarly, all
            information regarding the use of certain commercial medicinal
            products is related to the regulatory approval status of the
            respective medicinal product in the United States. In other
            countries, the respective approval status may differ. Users are
            advised to verify that the information is consistent with the
            regulations and guidelines applicable in their respective countries.
          </LegalParagraph>
        </LegalSubsection>
      </LegalSection>
      <LegalSection title="8. User Content">
        <LegalParagraph>
          The Platform may allow you to post, upload, or submit content, such as
          comments, questions, feedback, and other materials (“User Content”).
          By submitting User Content, you grant [Your Company Name] a
          non-exclusive, royalty-free, transferable, sublicensable, worldwide
          right and license to use, reproduce, modify, display, and distribute
          your User Content in connection with the Platform.
        </LegalParagraph>
        <LegalParagraph>
          You represent and warrant that:
          <UnorderedList>
            <ListItem>
              You own or have the necessary rights to use and authorize the
              Company to use all User Content you submit.
            </ListItem>
            <ListItem>
              Your User Content does not infringe, misappropriate, or violate
              any third-party rights or any laws.
            </ListItem>
          </UnorderedList>
        </LegalParagraph>
        <LegalParagraph>
          The Company reserves the right, but is not obligated, to review,
          screen, or delete User Content at our discretion.
        </LegalParagraph>
      </LegalSection>
      <LegalSection title="9. Termination and Suspension">
        <LegalParagraph>
          We reserve the right to terminate or suspend your account and access
          to the Platform at our sole discretion, without notice, for conduct
          that we believe is in violation of these Terms or harmful to the
          Platform, other users, or third parties. Upon termination:
          <UnorderedList>
            <ListItem>
              All rights and licenses granted to you will cease.
            </ListItem>
            <ListItem>You must cease all use of the Platform.</ListItem>
            <ListItem>
              Any outstanding fees and charges will remain due.
            </ListItem>
          </UnorderedList>
        </LegalParagraph>
      </LegalSection>
      <LegalSection title="10. Disclaimer of Warranties">
        <LegalParagraph>
          The Platform is provided on an "as is" and "as available" basis. To
          the fullest extent permitted by law, the Company disclaims all
          warranties, express or implied, including warranties of
          merchantability, fitness for a particular purpose, non-infringement,
          and any warranties arising out of course of dealing or usage of trade.
          We do not warrant that:
          <UnorderedList>
            <ListItem>
              The Platform will meet your specific requirements.
            </ListItem>
            <ListItem>
              The Platform will be uninterrupted, timely, secure, or error-free.
            </ListItem>
            <ListItem>
              The results obtained from using the Platform will be accurate or
              reliable.
            </ListItem>
            <ListItem>Any errors in the Platform will be corrected.</ListItem>
          </UnorderedList>
        </LegalParagraph>
      </LegalSection>
      <LegalSection title="11. Limitation of Liability">
        <LegalParagraph>
          To the maximum extent permitted by law, the Company will not be liable
          for any indirect, incidental, special, consequential, or punitive
          damages, or any loss of profits or revenues, whether incurred directly
          or indirectly, or any loss of data, use, goodwill, or other intangible
          losses, resulting from:
          <UnorderedList>
            <ListItem>Your use or inability to use the Platform.</ListItem>
            <ListItem>
              Any unauthorized access to or use of our servers and/or any
              personal information stored therein.
            </ListItem>
            <ListItem>
              Any interruption or cessation of transmission to or from our
              Platform.
            </ListItem>
            <ListItem>
              Any bugs, viruses, trojan horses, or the like that may be
              transmitted to or through our Platform by any third party.
            </ListItem>
            <ListItem>
              Any errors or omissions in any content or for any loss or damage
              incurred as a result of the use of any content posted, emailed,
              transmitted, or otherwise made available through the Platform.
            </ListItem>
          </UnorderedList>
        </LegalParagraph>
      </LegalSection>
      <LegalSection title="12. Indemnification">
        <LegalParagraph>
          You agree to indemnify, defend, and hold harmless the Company, its
          affiliates, and their respective officers, directors, employees,
          agents, and licensors from and against any and all claims,
          liabilities, damages, losses, and expenses, including reasonable
          attorneys' fees and costs, arising out of or in any way connected
          with:
          <UnorderedList>
            <ListItem>Your access to or use of the Platform.</ListItem>
            <ListItem>Your violation of these Terms.</ListItem>
            <ListItem>
              Your violation of any third-party right, including any
              intellectual property right.
            </ListItem>
            <ListItem>
              Any claim that your User Content caused damage to a third party.
            </ListItem>
          </UnorderedList>
        </LegalParagraph>
      </LegalSection>
      <LegalSection title="13. Modifications and Updates">
        <LegalParagraph>
          We reserve the right to modify these Terms at any time. Any changes to
          these Terms will be effective immediately upon posting on the
          Platform. Your continued use of the Platform after the changes have
          been posted signifies your acceptance of the revised Terms. It is your
          responsibility to review these Terms periodically for changes.
        </LegalParagraph>
      </LegalSection>
      <LegalSection title="14. Governing Law and Dispute Resolution">
        <LegalParagraph>
          These Terms are governed by and construed in accordance with the laws
          applicable to the Company’s headquarters, without regard to its
          conflict of law principles. Any legal action or proceeding arising
          under these Terms will be exclusively brought in courts located in the
          United Kingdom. You agree to resolve any dispute, claim, or
          controversy arising out of or in connection with these Terms or the
          breach, termination, enforcement, interpretation, or validity thereof,
          including the determination of the scope or applicability of this
          agreement to arbitrate, in a competent court located applicable to the
          Company’s headquarters.
        </LegalParagraph>
      </LegalSection>
      <LegalSection title="15. Miscellaneous">
        <VStack className="gap-4">
          <LegalParagraph>
            <b>Entire Agreement</b>: These Terms constitute the entire agreement
            between you and the Company regarding your use of the Platform and
            supersede all prior agreements and understandings.
          </LegalParagraph>
          <LegalParagraph>
            <b>Waiver and Severability</b>: Our failure to enforce any right or
            provision of these Terms will not be considered a waiver of those
            rights. If any provision of these Terms is held to be invalid or
            unenforceable, the remaining provisions will remain in full force
            and effect.
          </LegalParagraph>
          <LegalParagraph>
            <b>Assignment</b>: You may not assign or transfer these Terms, or
            any rights or obligations hereunder, without our prior written
            consent. We may freely assign these Terms without restriction.
          </LegalParagraph>
          <LegalParagraph>
            <b>No Agency</b>: No joint venture, partnership, employment, or
            agency relationship exists between you and the Company as a result
            of these Terms or your use of the Platform.
          </LegalParagraph>
        </VStack>
      </LegalSection>
      <LegalSection title="16. Contact Information">
        <LegalParagraph>
          If you have any questions, concerns, or feedback about these Terms,
          please contact us at:
        </LegalParagraph>
        <LegalParagraph>
          <ul>
            <li>MEDizzy LTD</li>
            <li>85 Uxbridge Road</li>
            <li>London W5 5TH</li>
            <li>United Kingdom</li>
            <li>contact@medizzy.com</li>
          </ul>
        </LegalParagraph>
        <VStack className="mt-8 gap-4 text-sm text-muted-foreground">
          <p>
            By using the Platform, you signify that you have read, understand,
            and agree to be bound by these Terms of Use. Thank you for choosing
            MEDizzy Learning as your trusted partner in Medical Licensing Exam
            preparation
          </p>
          <p>
            The United States Medical Licensing Examination (USMLE®) is a joint
            program of the Federation of State Medical Boards (FSMB®) and
            National Board of Medical Examiners (NBME®). NATIONALLY CERTIFIED
            EMERGENCY MEDICAL TECHNICIAN (NREMT®) is a trademark of National
            Registry of Emergency Medical Technicians. None of the trademark
            holders are affiliated with MEDizzy LTD.
          </p>
        </VStack>
      </LegalSection>
    </VStack>
  );
}

export const shouldRevalidate = () => false;

export default LegalTermsOfServicePage;
